import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  LayoutDashboard, Sun, Plane, MapPin, Landmark, Users2, Handshake,
  Bell, LogOut, Siren, ChevronDown, UserCog, X, ArrowLeftRight,
  Compass, CalendarRange, AlertOctagon, BedDouble, FileText, Sparkles, ClipboardList, Image, BarChart3,
  Wallet, UserCircle2, FileBarChart2, Package, ClipboardCheck, Menu, Download, Camera, Star, Users, HeartHandshake, KeyRound, BookOpen, Building2, Wrench, MessageCircle, Layers, Trash2, SkipForward, Sliders, Inbox
} from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { enableWebPush, currentSubscription, isPushSupported } from "../lib/webpush";
import { usePwaInstall } from "../lib/usePwaInstall";
import ChatWidget from "../components/ChatWidget";
import FirstLoginNotifPrompt from "../components/FirstLoginNotifPrompt";
import FirstLoginTaskNudge from "../components/FirstLoginTaskNudge";
import PendingPaymentsChip from "../components/PendingPaymentsChip";
import { useBrand } from "../lib/brand";

const ROLE_LABEL = {
  FOUNDER: "Founder / CEO", BDOM: "BD & Ops Manager",
  PCWM: "Placement & Cultural", GEHM: "Guest Exp & Housekeeping", SMCM: "Social Media & Content",
};

const NAV = [
  { to: "/my-day", label: "My Day", icon: Sun, roles: ["FOUNDER","BDOM","PCWM","GEHM","SMCM"] },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["FOUNDER","BDOM","PCWM","GEHM","SMCM"] },
  { to: "/chat", label: "Team Chat", icon: MessageCircle, roles: ["FOUNDER","BDOM","PCWM","GEHM","SMCM"] },
  { to: "/program-bookings", label: "Program Bookings", icon: Landmark, roles: ["FOUNDER","BDOM","PCWM","GEHM"], perm: "program_bookings" },
  { to: "/weekend-bookings", label: "Weekend Bookings", icon: MapPin, roles: ["FOUNDER","BDOM","PCWM","SMCM","GEHM"], perm: "weekend_bookings" },
  { to: "/weekend-tours", label: "Weekend Tours", icon: Compass, roles: ["FOUNDER","BDOM","PCWM","GEHM","SMCM"] },
  { to: "/arrivals", label: "Arrivals", icon: Plane, roles: ["FOUNDER","BDOM","GEHM","PCWM","SMCM"], perm: "arrivals" },
  { to: "/trips", label: "Weekend Tour Organizer", icon: Compass, roles: ["FOUNDER","BDOM"], perm: "trips" },
  { to: "/activities", label: "Activity Planner", icon: CalendarRange, roles: ["FOUNDER","BDOM","PCWM"], perm: "activities" },
  { to: "/rooms", label: "Accommodation", icon: BedDouble, roles: ["FOUNDER","BDOM","GEHM","PCWM"], perm: "rooms", pcwmOutsideIndiaOnly: true },
  { to: "/cforms", label: "C-Forms", icon: FileText, roles: ["FOUNDER","BDOM","GEHM"], perm: "cforms", indiaOnly: true },
  { to: "/housekeeping", label: "Housekeeping", icon: Sparkles, roles: ["FOUNDER","BDOM","GEHM"], perm: "housekeeping", indiaOnly: true },
  { to: "/inventory", label: "Inventory", icon: Package, roles: ["FOUNDER","BDOM","GEHM"], perm: "inventory" },
  { to: "/attendance", label: "Attendance & Salary", icon: ClipboardCheck, roles: ["FOUNDER","BDOM","PCWM","GEHM","SMCM"], perm: "attendance", indiaOnly: true },
  { to: "/content", label: "Content Calendar", icon: Image, roles: ["FOUNDER","BDOM","SMCM"], perm: "content" },
  { to: "/content-analytics", label: "Content Analytics", icon: BarChart3, roles: ["FOUNDER","BDOM","SMCM"], perm: "content_analytics" },
  { to: "/media", label: "Pictures & Videos", icon: Camera, roles: ["FOUNDER","BDOM","SMCM"], perm: "media" },
  { to: "/success-stories", label: "Success Stories", icon: Star, roles: ["FOUNDER","BDOM","SMCM"], perm: "success_stories" },
  { to: "/sales", label: "Sales & Invoices", icon: Users2, roles: ["FOUNDER"], perm: "sales" },
  { to: "/partners", label: "Partners in Progress", icon: Handshake, roles: ["FOUNDER","BDOM"], perm: "partners" },
  { to: "/leads", label: "Leads", icon: Users, roles: ["FOUNDER","BDOM"], perm: "leads" },
  { to: "/participant-ops", label: "Participant Ops", icon: HeartHandshake, roles: ["FOUNDER","BDOM","PCWM","GEHM","SMCM"], perm: "participant_ops" },
  { to: "/receipts-payments", label: "Receipts & Payments", icon: Wallet, roles: ["FOUNDER","BDOM","PCWM","GEHM","SMCM"], perm: "receipts_payments" },
  { to: "/settings", label: "Registries", icon: ClipboardList, roles: ["FOUNDER","BDOM"], perm: "settings" },
  { to: "/partner-payments", label: "Revenue Ledger", icon: Wallet, roles: ["FOUNDER","BDOM"] },
  { to: "/pending-payments", label: "Pending Approvals", icon: Wallet, roles: ["FOUNDER","BDOM"] },
  { to: "/nudge-thresholds", label: "Nudge Thresholds", icon: Sliders, roles: ["FOUNDER"] },
  { to: "/team", label: "Team", icon: UserCog, roles: ["FOUNDER"], perm: "team" },
  { to: "/participant-logins", label: "Participant Logins", icon: KeyRound, roles: ["FOUNDER"] },
  { to: "/partner-admin", label: "Partner Accounts", icon: Building2, roles: ["FOUNDER"] },
  { to: "/partner-programs", label: "Partner Programs", icon: Layers, roles: ["FOUNDER","BDOM"] },
  { to: "/staff-hub", label: "Staff Hub", icon: BookOpen, roles: ["FOUNDER","BDOM","PCWM","GEHM","SMCM"] },
  { to: "/approval-requests", label: "Approval Requests", icon: Inbox, roles: ["FOUNDER","BDOM","PCWM","GEHM","SMCM"] },
  { to: "/vendors", label: "Vendors", icon: Wrench, roles: ["FOUNDER","BDOM","GEHM"] },
  { to: "/reports", label: "Monthly Reports", icon: FileBarChart2, roles: ["FOUNDER"] },
  { to: "/recycle-bin", label: "Recycle Bin", icon: Trash2, roles: ["FOUNDER"] },
  { to: "/skip-history", label: "Skip History", icon: SkipForward, roles: ["FOUNDER"] },
  { to: "/profile", label: "My Profile", icon: UserCircle2, roles: ["FOUNDER","BDOM","PCWM","GEHM","SMCM"] },
];

export default function Layout() {
  const { user, logout, notifications, markAllRead, emergency, dismissEmergency, impersonate, stopImpersonation, wsConnected } = useAuth();
  const brand = useBrand();
  const [notifOpen, setNotifOpen] = useState(false);
  const [emerOpen, setEmerOpen] = useState(false);
  const [emerText, setEmerText] = useState("");
  const [impOpen, setImpOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [nag, setNag] = useState({ pending: 0, overdue: 0, top_overdue: [] });
  const { canInstall, install: doInstallPwa } = usePwaInstall();

  useEffect(() => {
    if (!isPushSupported()) return;
    currentSubscription().then((s) => setPushOn(!!s));
  }, []);

  // Poll pending-task summary every 15 minutes; show a persistent nag when
  // the user has overdue tasks (so they're gently reminded on their dashboard
  // until they close them). Also runs once on mount + shortly after login.
  useEffect(() => {
    if (!user || user.role === "PARTICIPANT" || user.role === "PARTNER") return;
    const tick = async () => {
      try { const { data } = await api.get("/tasks/my-pending-summary"); setNag(data); }
      catch (err) { console.debug("nag poll failed:", err?.message); }
    };
    tick();
    const id = setInterval(tick, 15 * 60 * 1000);   // every 15 min
    return () => clearInterval(id);
  }, [user?.id, user?.role]);

  const doEnablePush = async () => {
    try {
      await enableWebPush();
      setPushOn(true);
      toast.success("Background push enabled");
    } catch (e) { toast.error(e?.message || "Could not enable push"); }
  };

  // PWA install prompt now handled by the shared `usePwaInstall` hook.

  const role = user.effective_role;
  const perms = user.permissions || [];
  // Only-India offices historically own Housekeeping + Attendance/Salary.
  // Other-country staff (PCWM Nepal, GEHM Vietnam, …) shouldn't see those
  // menus until Founder explicitly turns them on via a permission.
  const userDest = (user.destination || "").trim().toLowerCase();
  const isIndiaOffice = !userDest || userDest === "india";
  const navItems = useMemo(
    () => NAV.filter(n => {
      const roleOk = n.roles.includes(role) || (n.perm && perms.includes(n.perm));
      if (!roleOk) return false;
      if (n.indiaOnly && !isIndiaOffice && !["FOUNDER", "BDOM"].includes(role) && !(n.perm && perms.includes(n.perm))) {
        return false;
      }
      // `pcwmOutsideIndiaOnly` — reverse of indiaOnly for the Accommodation
      // menu: India-based PCWMs never touch accommodation (GEHM does), only
      // out-of-India PCWM offices need this menu. Founder/BDOM/GEHM keep it
      // always. If the perm is explicitly granted, it also unlocks.
      if (n.pcwmOutsideIndiaOnly && role === "PCWM" && isIndiaOffice && !(n.perm && perms.includes(n.perm))) {
        return false;
      }
      return true;
    }),
    [role, perms, isIndiaOffice]
  );
  const unread = notifications.filter(n => !n.read).length;

  // Close mobile drawer whenever the route changes (NavLink is clicked)
  const closeMobile = () => setMobileNavOpen(false);
  // Lock body scroll when drawer is open on mobile
  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileNavOpen]);

  const sendEmergency = async () => {
    if (!emerText.trim()) return;
    await api.post("/emergency", { message: emerText });
    toast.error("Emergency broadcast sent");
    setEmerText(""); setEmerOpen(false);
  };

  return (
    <div className="min-h-screen">
      {emergency && (
        <div data-testid="emergency-banner" className="pulse-red text-white sticky top-0 z-50 flex flex-wrap items-center justify-between gap-2 px-4 sm:px-6 py-3 shadow-lg">
          <div className="flex items-center gap-2 sm:gap-3 font-semibold min-w-0 flex-1">
            <Siren size={20} className="animate-pulse shrink-0" />
            <span className="uppercase tracking-wider text-xs shrink-0">Emergency</span>
            <span className="text-sm sm:text-base truncate">{emergency.message}</span>
            <span className="hidden sm:inline text-xs opacity-75 shrink-0">— {emergency.sender_name} ({emergency.sender_role})</span>
          </div>
          {user.role === "FOUNDER" && (
            <button data-testid="dismiss-emergency" onClick={() => dismissEmergency(emergency.id)}
              className="text-white/90 hover:text-white text-xs bg-white/10 hover:bg-white/20 px-3 py-1 rounded-md shrink-0">Dismiss</button>
          )}
        </div>
      )}

      {user.impersonating && (
        <div className="bg-amber-100 border-b border-amber-300 text-amber-900 text-xs sm:text-sm px-4 sm:px-6 py-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2"><ArrowLeftRight size={14} /> Viewing as <strong>{ROLE_LABEL[user.impersonating]}</strong> — Founder impersonation.</div>
          <button data-testid="stop-impersonation" onClick={stopImpersonation} className="text-amber-900 font-semibold hover:underline">Return to Founder view</button>
        </div>
      )}

      {nag.overdue > 0 && (
        <div className="bg-red-50 border-b border-red-200 text-red-900 text-xs sm:text-sm px-4 sm:px-6 py-2 flex flex-wrap items-center justify-between gap-2" data-testid="task-nag-banner">
          <div className="flex items-center gap-2">
            <Bell size={14} className="text-red-700"/>
            <span><b>{nag.overdue} overdue task{nag.overdue === 1 ? "" : "s"}</b> — {nag.top_overdue.slice(0, 2).map(t => t.title).join(" · ")}{nag.top_overdue.length > 2 ? " · …" : ""}</span>
          </div>
          <NavLink to="/my-day" className="text-red-800 font-semibold hover:underline">Open my day →</NavLink>
        </div>
      )}

      <div className="flex">
        {/* Mobile drawer backdrop */}
        {mobileNavOpen && (
          <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={closeMobile} data-testid="mobile-nav-backdrop" />
        )}

        <aside
          data-testid="app-sidebar"
          className={`w-[260px] shrink-0 bg-white border-r border-[color:var(--aii-border)] p-5 flex flex-col fixed lg:sticky top-0 z-50 h-screen transition-transform duration-200
            ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
          <div className="flex items-center justify-between gap-3 mb-8">
            <div className="flex items-center gap-3 min-w-0">
              {/* Logo is optional — only rendered when brand.logo_url
                  is set. Keeps the sidebar clean for the generic /
                  white-label install where a tenant hasn't uploaded a
                  logo yet. */}
              {brand.logo_url && (
                <img src={brand.logo_url} alt={brand.name || "Brand"} className="w-12 h-12 object-contain shrink-0" data-testid="brand-logo" />
              )}
              <div className="min-w-0">
                <div className="font-display text-lg font-bold leading-none">
                  {brand.wordmark || (brand.name || "").toUpperCase()}
                  {!brand.hide_vendor_wordmark && <span className="text-brand">®</span>}
                </div>
                {brand.tagline && <div className="text-xs text-[color:var(--aii-muted)] mt-1">{brand.tagline}</div>}
              </div>
            </div>
            <button data-testid="mobile-nav-close" onClick={closeMobile} className="lg:hidden p-1 rounded-md hover:bg-[color:var(--aii-green-tint)]"><X size={18}/></button>
          </div>

          <nav className="space-y-1 flex-1 overflow-y-auto pr-1">
            <PendingPaymentsChip/>
            {navItems.map(({to,label,icon:Icon}) => (
              <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g,'-')}`}
                onClick={closeMobile}
                className={({isActive}) => `aii-sidebar-item ${isActive ? "active" : ""}`}>
                <Icon size={17} strokeWidth={1.75} />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>

          {user.role === "FOUNDER" && (
            <div className="mt-4 relative">
              <button data-testid="impersonate-btn" onClick={() => setImpOpen(o=>!o)} className="w-full aii-sidebar-item justify-between">
                <span className="flex items-center gap-2"><UserCog size={17} /> View as…</span>
                <ChevronDown size={14} />
              </button>
              {impOpen && (
                <div className="absolute bottom-full mb-2 left-0 right-0 aii-card p-2 z-30">
                  {["FOUNDER","BDOM","PCWM","GEHM","SMCM"].map(r => (
                    <button key={r} data-testid={`imp-${r}`}
                      onClick={() => { setImpOpen(false); r === "FOUNDER" ? stopImpersonation() : impersonate(r); }}
                      className="block w-full text-left px-3 py-1.5 rounded hover:bg-[color:var(--aii-green-tint)] text-sm">
                      {ROLE_LABEL[r]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <button data-testid="logout-btn" onClick={logout}
            className="mt-3 aii-sidebar-item text-[color:var(--aii-text-2)]">
            <LogOut size={17} /><span>Sign out</span>
          </button>

          {canInstall && (
            <button data-testid="pwa-install" onClick={doInstallPwa}
              className="mt-2 flex items-center justify-center gap-2 bg-brand text-white text-sm font-semibold px-3 py-2 rounded-md hover:bg-brand-dark">
              <Download size={14} /> Install app
            </button>
          )}
        </aside>

        <main className="flex-1 min-w-0">
          <header className="bg-white border-b border-[color:var(--aii-border)] px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-3 sticky top-0 z-30">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <button data-testid="mobile-nav-open" onClick={() => setMobileNavOpen(true)}
                className="lg:hidden p-2 rounded-md hover:bg-[color:var(--aii-green-tint)] shrink-0">
                <Menu size={20} />
              </button>
              <div className="min-w-0">
                <div className="text-[10px] sm:text-xs uppercase tracking-widest text-[color:var(--aii-muted)] hidden sm:block">Signed in as</div>
                <div className="font-display font-semibold text-sm sm:text-base truncate">
                  {user.name} · <span className="text-brand">{ROLE_LABEL[role]}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
              <span title={wsConnected ? "Real-time connected" : "Reconnecting…"} className={`hidden sm:block h-2 w-2 rounded-full ${wsConnected ? 'bg-emerald-500' : 'bg-amber-500'} animate-pulse`} data-testid="ws-indicator"/>
              <button data-testid="emergency-open" onClick={() => setEmerOpen(true)}
                className="text-xs font-semibold text-red-700 border border-red-200 bg-red-50 hover:bg-red-100 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md flex items-center gap-1.5">
                <Siren size={14} /> <span className="hidden sm:inline">Emergency</span>
              </button>
              <div className="relative">
                <button data-testid="notif-bell" onClick={() => { setNotifOpen(o=>!o); if(!notifOpen) markAllRead(); }} className="relative p-2 hover:bg-[color:var(--aii-green-tint)] rounded-md">
                  <Bell size={18} />
                  {unread > 0 && <span data-testid="notif-count" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 text-[10px] bg-red-600 text-white rounded-full flex items-center justify-center font-bold">{unread}</span>}
                </button>
                {notifOpen && (
                  <div className="absolute right-0 top-full mt-2 w-[min(24rem,calc(100vw-2rem))] bg-white border border-[color:var(--aii-border)] rounded-lg shadow-xl z-40 max-h-[70vh] overflow-auto">
                    <div className="p-3 border-b border-[color:var(--aii-border)] font-semibold text-sm flex justify-between items-center">
                      <span>Notifications</span>
                      <button onClick={() => setNotifOpen(false)}><X size={14} /></button>
                    </div>
                    {isPushSupported() && (
                      <div className="p-3 border-b border-[color:var(--aii-border)] flex items-center justify-between text-xs bg-[color:var(--aii-green-tint)]/40">
                        <div>
                          <div className="font-semibold text-[color:var(--aii-text-1)]">Background push</div>
                          <div className="text-[color:var(--aii-muted)]">{pushOn ? "Alerts even when the app is closed." : "Enable to get alerts on your phone/desktop even when the app is closed."}</div>
                        </div>
                        {pushOn ? (
                          <span data-testid="push-on-badge" className="text-xs px-2 py-1 bg-emerald-100 text-emerald-800 rounded flex items-center gap-1"><Bell size={11}/> On</span>
                        ) : (
                          <button onClick={doEnablePush} data-testid="enable-push-btn" className="text-xs px-2 py-1 bg-brand text-white rounded hover:bg-brand-dark font-semibold">Enable</button>
                        )}
                      </div>
                    )}
                    {notifications.length === 0 && <div className="p-6 text-center text-sm text-[color:var(--aii-muted)]">Nothing yet.</div>}
                    {notifications.map(n => (
                      <div key={n.id} className={`p-3 border-b border-[color:var(--aii-border)] text-sm ${n.kind==='emergency' ? 'bg-red-50' : ''}`}>
                        <div>{n.message}</div>
                        <div className="text-xs text-[color:var(--aii-muted)] mt-1">{new Date(n.created_at).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </header>

          <div className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>

      <ChatWidget />
      <FirstLoginNotifPrompt userId={user?.id}/>
      <FirstLoginTaskNudge userId={user?.id}/>

      {emerOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center gap-2 mb-3">
              <Siren size={22} className="text-red-600" />
              <h3 className="text-lg font-semibold">Broadcast Emergency</h3>
            </div>
            <p className="text-sm text-[color:var(--aii-text-2)] mb-3">This will send a red banner + beep to <strong>every logged-in user</strong>.</p>
            <textarea data-testid="emergency-text" value={emerText} onChange={e=>setEmerText(e.target.value)} rows={3}
              placeholder="Describe the emergency briefly…"
              className="w-full border border-[color:var(--aii-border)] rounded-md p-2 focus:ring-2 focus:ring-red-400/30 focus:border-red-500"/>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEmerOpen(false)} className="px-4 py-2 text-sm">Cancel</button>
              <button data-testid="emergency-send" onClick={sendEmergency} className="bg-red-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-red-700">Broadcast</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
